// Lexical document -> the `TeamsBlockNode[]` wire model. The only translation
// between the editor and the send path.
//
// ─── WHY THIS IS ITS OWN MODULE ──────────────────────────────────────────────
//
// The composer is a React component full of selection, focus and IME concerns
// that jsdom cannot exercise. The conversion is pure: an editor state in, a
// plain data structure out, no DOM. Keeping it separate is what makes the part
// that actually decides what other people receive exhaustively testable.
//
// ─── THE TWO RULES ───────────────────────────────────────────────────────────
//
// 1. NEVER THROW. Anything this does not recognise degrades to its text
//    content. A node type nobody anticipated (a paste from Word, a future
//    plugin, a decorator) must cost the user their formatting at worst — never
//    their message, and never an exception on the send path.
// 2. NEVER EMIT HTML. This produces data. `teamsNodesToHtml` in @atlas/shared,
//    server-side, is the only place markup is constructed.

import {
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  type EditorState,
  type LexicalNode,
} from 'lexical';
import { $isListNode, $isListItemNode } from '@lexical/list';
import { $isLinkNode } from '@lexical/link';
import { $isMentionNode } from './MentionNode';
import {
  TEAMS_COMPOSE_MAX_BLOCKS,
  TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK,
  TEAMS_COMPOSE_MAX_LIST_ITEMS,
  isAllowedLinkHref,
  type TeamsBlockNode,
  type TeamsInlineNode,
  type TeamsTextMarks,
} from '@atlas/shared';

/** Lexical's text-format flags, mapped onto the wire model's mark names. */
function marksOf(node: LexicalNode): TeamsTextMarks {
  if (!$isTextNode(node)) return {};
  const marks: TeamsTextMarks = {};
  if (node.hasFormat('bold')) marks.bold = true;
  if (node.hasFormat('italic')) marks.italic = true;
  if (node.hasFormat('underline')) marks.underline = true;
  // `strikethrough` in Lexical, `strike` on the wire — Teams' own tag is `<s>`.
  if (node.hasFormat('strikethrough')) marks.strike = true;
  if (node.hasFormat('code')) marks.code = true;
  return marks;
}

function sameMarks(a: TeamsTextMarks, b: TeamsTextMarks): boolean {
  return (
    !!a.bold === !!b.bold &&
    !!a.italic === !!b.italic &&
    !!a.underline === !!b.underline &&
    !!a.strike === !!b.strike &&
    !!a.code === !!b.code
  );
}

/**
 * Append a text run, merging into the previous one when the marks match.
 *
 * Lexical splits text on every format boundary and on every edit, so a plain
 * sentence can arrive as a dozen TextNodes. Merging keeps the payload honest
 * and keeps the per-block cap measuring content rather than edit history.
 */
function pushText(into: TeamsInlineNode[], text: string, marks: TeamsTextMarks): void {
  if (text === '') return;
  const previous = into[into.length - 1];
  if (previous && previous.type === 'text' && sameMarks(previous, marks)) {
    previous.text += text;
    return;
  }
  into.push({ type: 'text', text, ...marks });
}

/**
 * The inline content of one block, flattened.
 *
 * Nested inline structure (a link inside bold inside a paragraph) collapses to
 * a flat run list, which is exactly what the wire model is.
 */
function collectInline(node: LexicalNode, into: TeamsInlineNode[]): void {
  if (into.length >= TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK) return;

  // BEFORE the TextNode branch: `MentionNode` extends `TextNode`, so the
  // generic check would swallow it and the userId — the only part Graph needs
  // to actually notify anybody — would never reach the wire.
  if ($isMentionNode(node)) {
    const text = node.getTextContent();
    const target = node.getTarget();
    const id = target.kind === 'user' ? target.userId : target.conversationId;
    // A mention with no id cannot be delivered as one. It degrades to its own
    // text rather than travelling as a mention Graph will silently drop: the
    // message still reads correctly, it simply does not notify.
    if (text === '' || id === '') {
      pushText(into, text, marksOf(node));
      return;
    }
    // No marks: a mention is styled by the recipient's Teams client, and
    // carrying bold/italic here would be formatting the client overrides
    // anyway. The wire model has no mark fields on this node for that reason.
    //
    // The leading `@` is EDITOR CHROME and is stripped here. Teams' own
    // messages carry `<at>Anthony Chiappone</at>`, not `<at>@Anthony…</at>`,
    // and the same string becomes `mentioned.user.displayName` on the wire —
    // where "@David Gooch" would be claiming that is the person's name.
    const label = text.replace(/^@/, '') || text;
    into.push(
      target.kind === 'user'
        ? { type: 'mention', userId: target.userId, text: label }
        : { type: 'mention', conversationId: target.conversationId, text: label },
    );
    return;
  }

  if ($isTextNode(node)) {
    pushText(into, node.getTextContent(), marksOf(node));
    return;
  }

  // A soft break inside a paragraph is content, not structure: Shift+Enter is
  // how people write a two-line message, and dropping it would silently join
  // the lines.
  if ($isLineBreakNode(node)) {
    pushText(into, '\n', {});
    return;
  }

  if ($isLinkNode(node)) {
    const href = node.getURL();
    const text = node.getTextContent();
    if (text === '') return;
    // Marks come from the first text run inside the link — a link is one run on
    // the wire, so a partially-bolded anchor picks the leading style.
    const firstText = node.getChildren().find((child) => $isTextNode(child));
    const marks = firstText ? marksOf(firstText) : {};
    // A scheme we will not vouch for never becomes a link node. It degrades to
    // its own text, so nothing the user typed disappears, but no href travels.
    // The serializer re-checks this server-side; this is the first of two gates,
    // not the only one.
    if (!isAllowedLinkHref(href)) {
      pushText(into, text, marks);
      return;
    }
    into.push({ type: 'link', href, text, ...marks });
    return;
  }

  if ($isElementNode(node)) {
    for (const child of node.getChildren()) {
      if (into.length >= TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK) return;
      collectInline(child, into);
    }
    return;
  }

  // Decorators and anything else unknown: take whatever text it can give.
  pushText(into, node.getTextContent(), {});
}

function inlineChildrenOf(node: LexicalNode): TeamsInlineNode[] {
  const inline: TeamsInlineNode[] = [];
  if ($isElementNode(node)) {
    for (const child of node.getChildren()) collectInline(child, inline);
  } else {
    collectInline(node, inline);
  }
  return inline.slice(0, TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK);
}

/**
 * A Lexical list, flattened to a single level.
 *
 * Lexical nests by putting a ListNode *inside* a ListItemNode. Teams' HTML has
 * no nesting worth targeting — verified against live Graph, a nested `<ul>`
 * renders inconsistently — so a nested item is promoted to a top-level item of
 * the outer list. Losing one level of indentation is a far better outcome than
 * emitting markup Teams renders unpredictably.
 */
function collectListItems(
  list: LexicalNode,
  into: { children: TeamsInlineNode[] }[],
): void {
  if (!$isElementNode(list)) return;
  for (const item of list.getChildren()) {
    if (into.length >= TEAMS_COMPOSE_MAX_LIST_ITEMS) return;
    if (!$isListItemNode(item)) {
      // A stray non-item child still carries text; keep it rather than lose it.
      const children = inlineChildrenOf(item);
      if (children.length > 0) into.push({ children });
      continue;
    }

    // An item that only wraps a nested list contributes no line of its own.
    const nested = item.getChildren().filter((child) => $isListNode(child));
    const ownInline: TeamsInlineNode[] = [];
    for (const child of item.getChildren()) {
      if ($isListNode(child)) continue;
      collectInline(child, ownInline);
    }
    if (ownInline.length > 0) {
      into.push({ children: ownInline.slice(0, TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK) });
    }
    for (const child of nested) collectListItems(child, into);
  }
}

function blockFor(node: LexicalNode): TeamsBlockNode | null {
  if ($isListNode(node)) {
    const items: { children: TeamsInlineNode[] }[] = [];
    collectListItems(node, items);
    if (items.length === 0) return null;
    return { type: 'list', ordered: node.getListType() === 'number', items };
  }

  // Matched by type name rather than by `$isCodeNode`/`$isQuoteNode` so this
  // module stays free of the node packages: it must be able to serialize a
  // document produced by any composer, including one whose plugins it does not
  // import.
  const type = node.getType();

  if (type === 'code') {
    // Whitespace IS the content of a code block, so it is taken verbatim.
    // `getTextContent` renders LineBreakNodes as "\n", which is what we want.
    return { type: 'codeBlock', text: node.getTextContent() };
  }

  if (type === 'quote') {
    return { type: 'quote', children: inlineChildrenOf(node) };
  }

  // Paragraphs, headings, and anything unrecognised: a paragraph of its inline
  // content. Teams has no headings, so an h2 pasted in becomes a line of text —
  // which is what it would have looked like as plain text anyway.
  return { type: 'paragraph', children: inlineChildrenOf(node) };
}

/**
 * Convert a Lexical root's children to the wire model. **Must be called inside
 * a Lexical read context** (`editorState.read`) — the `$` prefix is Lexical's
 * convention for exactly that.
 */
export function $rootToTeamsNodes(root: LexicalNode): TeamsBlockNode[] {
  if (!$isElementNode(root)) return [];
  const blocks: TeamsBlockNode[] = [];
  for (const child of root.getChildren()) {
    if (blocks.length >= TEAMS_COMPOSE_MAX_BLOCKS) break;
    const block = blockFor(child);
    if (block) blocks.push(block);
  }
  return blocks;
}

/**
 * Convert an `EditorState` to the wire model. The entry point the composer uses;
 * it opens the read context itself.
 */
export function lexicalToTeamsNodes(editorState: EditorState): TeamsBlockNode[] {
  return editorState.read(() => $rootToTeamsNodes($getRoot()));
}
